// Thin fetch wrapper for the gnss-api backend.
//
// Two responsibilities:
//   1. Stuff the Telegram WebApp ``initData`` into the
//      ``X-Telegram-Init-Data`` header on every request.
//   2. Convert non-2xx responses into typed ``ApiError``s so React
//      Query surfaces them cleanly.
//
// All endpoints live under ``/api/v1/...``. In production the frontend
// is on the same subdomain as the API (Caddy reverse-proxies
// ``/api/*`` to gnss-api on :8001), so a relative URL is enough.
// In local dev Vite's proxy (see vite.config.ts) forwards ``/api/*``
// to the live VPS.

import type {
  AnalyticsActivityResponse,
  AnalyticsAvailableConfigsResponse,
  AnalyticsFilters,
  AnalyticsHeatmap,
  AnalyticsScatterResponse,
  AnalyticsSummary,
  MapStationsResponse,
  BackfillPlanRequest,
  BackfillPlanResponse,
  BackfillRunRequest,
  BackfillRunResponse,
  BatchCreateRequest,
  BatchCreateResponse,
  BatchSummary,
  ConvergenceAggregateResponse,
  ConvergenceCurveResponse,
  ConvergenceHeatmapData,
  ConvergencePrepAccepted,
  ConvergencePrepStatus,
  ConvergenceRunRequest,
  ConvergenceSeriesListResponse,
  EventFeedResponse,
  FailureBreakdownResponse,
  EventListResponse,
  FeedCategory,
  FeedSeverity,
  JobDetail,
  JobListResponse,
  JobState,
  NotificationSettings,
  NotificationSettingsUpdate,
  PoolAvailabilityResponse,
  ProcessingTimeResponse,
  SchedulerStatusResponse,
  SelfAssessmentResponse,
  Severity,
  StatusResponse,
  UserSettingsResponse,
  UserSettingsUpdateRequest,
} from './types';

const API_BASE = '/api/v1';

/** Thrown on any non-2xx response. ``message`` carries the server's
 *  ``detail`` field where available, falling back to the HTTP status text. */
export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function getInitData(): string {
  return window.Telegram?.WebApp?.initData ?? '';
}

// Phase 3.14 — ``boolean`` added for the ``/batches`` ``include_completed``
// query param. ``buildUrl`` stringifies via ``String(value)`` so
// ``true`` → ``"true"`` is what FastAPI's ``Query(bool)`` expects.
type QueryValue = string | string[] | number | boolean | undefined | null;

/** Compose the request URL, appending repeated params for array values
 *  (so ``?state=done&state=failed`` is emitted, matching the FastAPI
 *  ``list[JobState]`` parser). Public for testability. */
export function buildUrl(path: string, params?: Record<string, QueryValue>): string {
  const url = new URL(API_BASE + path, window.location.origin);
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === null) continue;
      if (Array.isArray(value)) {
        for (const v of value) {
          url.searchParams.append(key, String(v));
        }
      } else {
        url.searchParams.set(key, String(value));
      }
    }
  }
  return url.toString();
}

interface RequestInit_<TBody = unknown> {
  method?: 'GET' | 'POST' | 'PATCH';
  body?: TBody;
  /** Hard ceiling for the round-trip (ms). On expiry the fetch is
   *  aborted and an ``ApiError(0, …)`` is thrown so React Query shows a
   *  real error instead of a forever-spinner on a dead/slow network.
   *  Defaults to {@link DEFAULT_TIMEOUT_MS}. The async-create gate runs
   *  ~10s server-side, so callers of it pass a larger value. */
  timeoutMs?: number;
}

/** Default per-request timeout. Generous enough for the slowest normal
 *  read, short enough that a hung request surfaces to the operator. */
const DEFAULT_TIMEOUT_MS = 30_000;

async function request<T>(
  path: string,
  params?: Record<string, QueryValue>,
  init: RequestInit_ = {},
): Promise<T> {
  const method = init.method ?? 'GET';
  const headers: Record<string, string> = {
    'X-Telegram-Init-Data': getInitData(),
  };
  let body: string | undefined;
  if (init.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(init.body);
  }
  const timeoutMs = init.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  let response: Response;
  try {
    response = await fetch(buildUrl(path, params), {
      method,
      headers,
      body,
      signal: controller.signal,
    });
  } catch (err) {
    if (controller.signal.aborted) {
      throw new ApiError(0, `Запрос превысил ${Math.round(timeoutMs / 1000)} с`);
    }
    throw new ApiError(0, err instanceof Error ? err.message : 'Сетевая ошибка');
  } finally {
    clearTimeout(timer);
  }

  if (!response.ok) {
    let detail = response.statusText;
    try {
      const parsed = (await response.json()) as { detail?: string };
      if (parsed.detail) detail = parsed.detail;
    } catch {
      // Body wasn't JSON — keep the status text.
    }
    throw new ApiError(response.status, detail);
  }

  return (await response.json()) as T;
}

export const api = {
  health: () => request<{ status: string; version: string }>('/health'),

  status: () => request<StatusResponse>('/status'),

  listJobs: (params?: {
    state?: JobState[];
    tag?: string;
    station?: string;
    search?: string;
    /** Phase 6.5 A — drill-down from BatchCard «ОШИБКИ» block. */
    failed_category?: string;
    /** Phase R — drill-down from the «к разбору» counter / popover
     *  (R-taxonomy, indexed membership on Job.failure_category).
     *  ``string[]`` emits a repeated query param (OR semantics). */
    failure_category?: string | string[];
    /** Phase 6.5 B — drill-down from «N без эталона» badge. */
    residual_skip?: boolean;
    /** Phase 6.5 C — drill-down from per-config row click. */
    config?: string;
    limit?: number;
    offset?: number;
  }) => request<JobListResponse>('/jobs', params),

  getJob: (jobId: number) => request<JobDetail>(`/jobs/${jobId}`),

  listEvents: (params?: {
    from?: string;
    to?: string;
    job_id?: number;
    severity?: Severity[];
    event_type?: string;
    limit?: number;
    offset?: number;
  }) => request<EventListResponse>('/events', params),

  /** Phase 6.9 C2 — curated «Лента событий»: whitelisted + grouped
   *  events. Separate from `listEvents` (the raw journal that backs
   *  the DetailScreen job timeline). */
  listEventFeed: (params?: {
    categories?: FeedCategory[];
    severity?: FeedSeverity[];
    since?: string;
    until?: string;
    limit?: number;
  }) => request<EventFeedResponse>('/events/feed', params),

  // ----- Phase 3.5 -----

  cancelJob: (jobId: number) =>
    request<JobDetail>(`/jobs/${jobId}/cancel`, undefined, { method: 'POST' }),

  retryJob: (jobId: number) =>
    request<JobDetail>(`/jobs/${jobId}/retry`, undefined, { method: 'POST' }),

  createBatch: (payload: BatchCreateRequest) =>
    request<BatchCreateResponse>('/batch', undefined, {
      method: 'POST',
      body: payload,
    }),

  // ----- Phase 6.3 — backfill plan + run endpoints -----

  /** Phase 6.3 C+D — read-only preview of a backfill plan. The
   *  BatchModal «Восполнение» mode calls this on every parameter
   *  change (debounced ~500 ms) to refresh the preview pane. The
   *  response carries the total task count + exclusion breakdown
   *  + first-10 sample + estimated duration. */
  planBackfill: (payload: BackfillPlanRequest) =>
    request<BackfillPlanResponse>('/series/plan-backfill', undefined, {
      method: 'POST',
      body: payload,
    }),

  /** Phase 6.3 C+D — actually create the planned tasks. Same payload
   *  as `planBackfill` plus the operator-chosen `tag` and a `confirm`
   *  flag. Returns the new job IDs + tag. The backend re-plans
   *  inside the transaction so the preview can't drift between
   *  «click Запустить» and «rows land». */
  runBackfill: (payload: BackfillRunRequest) =>
    request<BackfillRunResponse>('/series/run-backfill', undefined, {
      method: 'POST',
      body: payload,
    }),

  /** Wave 4b — async create. The backend runs the cheap SINEX gate
   *  synchronously then returns 202 with a ``batch_id``; the slow
   *  fetch+slice+create happens in a scheduler daemon. Poll
   *  {@link getPrepStatus} for «готовлю N/M» progress. The gate's CDDIS
   *  probe is bounded at ~10s server-side, so we allow 20s here. */
  runConvergenceAsync: (payload: ConvergenceRunRequest) =>
    request<ConvergencePrepAccepted>('/series/run-convergence-async', undefined, {
      method: 'POST',
      body: payload,
      timeoutMs: 20_000,
    }),

  /** Wave 4b — progress of one async-create batch. ``status`` is
   *  ``PREPARING`` until every station is prepared/failed; the prepared
   *  series then appear in the normal batches poll. */
  getPrepStatus: (batchId: string) =>
    request<ConvergencePrepStatus>(
      `/series/prep/${encodeURIComponent(batchId)}`,
    ),

  /** Phase C-ui — list of every convergence-test series ever
   *  launched. Each row carries station/date/configs + counts so
   *  the Analytics convergence-tab picker can show one card per
   *  series. */
  listConvergenceSeries: () =>
    request<ConvergenceSeriesListResponse>(
      '/analytics/convergence/series',
    ),

  /** Phase C-ui — per-slice curve points for one convergence series.
   *  Returns a point per slice with both |dR| (primary metric) and
   *  σ3D (service self-estimate, shown side-by-side) plus the time
   *  the slice spent on PPP. */
  getConvergenceCurve: (tag: string, config?: string) =>
    request<ConvergenceCurveResponse>(
      `/analytics/convergence/series/${encodeURIComponent(tag)}/curve`,
      config ? { config } : undefined,
    ),

  /** Phase C-agg — aggregated convergence curve (median |dR| per config
   *  over the nominal-length grid) + honest series breakdown. Optional
   *  `fromDate` / `toDate` (ISO `YYYY-MM-DD`) bound the slice observation
   *  date. Threshold / «время до порога» are derived client-side. */
  getConvergenceAggregate: (fromDate?: string, toDate?: string) =>
    request<ConvergenceAggregateResponse>(
      '/analytics/convergence/aggregate',
      fromDate || toDate
        ? {
            ...(fromDate ? { from_date: fromDate } : {}),
            ...(toDate ? { to_date: toDate } : {}),
          }
        : undefined,
    ),

  /** Phase C-viz — per (station × config) median |dR| curve + honest state
   *  for the «Сходимость» matrix. Same `fromDate` / `toDate` bounds as the
   *  aggregate. The matrix metric (hours-to-threshold ↔ |dR|@24h) and the
   *  time-to-threshold histogram are derived client-side from these curves. */
  getConvergenceHeatmap: (fromDate?: string, toDate?: string) =>
    request<ConvergenceHeatmapData>(
      '/analytics/convergence/heatmap',
      fromDate || toDate
        ? {
            ...(fromDate ? { from_date: fromDate } : {}),
            ...(toDate ? { to_date: toDate } : {}),
          }
        : undefined,
    ),

  /** Phase R — full DB breakdown by failure category (and state).
   *  Powers the Main-screen counter row and the «Провалы» section
   *  on the Daily analytics tab. Cheap aggregate query, safe to
   *  call at the 30-s analytics tick. */
  getFailureBreakdown: () =>
    request<FailureBreakdownResponse>('/stats/failure-breakdown'),

  /** Phase 4.14 — pre-batch pool inspection. The BatchModal calls
   *  this when both ``obs_dates`` and ``tectonic_model`` are known
   *  so it can show «Доступно: X (исключено Y)» before the user
   *  commits to a random-pool batch. */
  getPoolAvailability: (params: {
    obs_dates: string[];
    tectonic_model: string;
  }) =>
    request<PoolAvailabilityResponse>('/pool/availability', params),

  // ----- Phase 3.14 — batch dashboard -----

  listBatches: (params?: {
    limit?: number;
    include_completed?: boolean;
    only_recent_hours?: number;
  }) => request<BatchSummary[]>('/batches', params),

  /** Phase 4.13.1 — feed-side batch summaries. Returns
   *  `BatchSummary` carrying the new fields (substitution_pairs,
   *  total_unique_stations, etc.) that drive the per-batch card
   *  rendered between task-cards in the main feed. Default
   *  ``state="completed"`` so active batches stay in the
   *  top-of-feed dashboard (no dup with /batches). */
  listBatchSummaries: (params?: {
    state?: 'active' | 'completed' | 'all';
    limit?: number;
    only_recent_hours?: number;
  }) => request<BatchSummary[]>('/batches/summaries', params),

  pauseScheduler: () =>
    request<SchedulerStatusResponse>('/scheduler/pause', undefined, {
      method: 'POST',
    }),

  resumeScheduler: () =>
    request<SchedulerStatusResponse>('/scheduler/resume', undefined, {
      method: 'POST',
    }),

  // ----- Phase 3.6 — per-user settings.

  getUserSettings: () =>
    request<UserSettingsResponse>('/user/settings'),

  updateUserSettings: (payload: UserSettingsUpdateRequest) =>
    request<UserSettingsResponse>('/user/settings', undefined, {
      method: 'PATCH',
      body: payload,
    }),

  // ----- Phase 6.9 C3 — per-type notification toggles.

  getNotificationSettings: () =>
    request<NotificationSettings>('/settings/notifications'),

  updateNotificationSettings: (payload: NotificationSettingsUpdate) =>
    request<NotificationSettings>('/settings/notifications', undefined, {
      method: 'POST',
      body: payload,
    }),

  /** Plain-URL helper for the rare case where a consumer wants the raw
   *  endpoint (e.g. for an ``<a href>`` deep-link from a popup). The
   *  default download path is :func:`downloadFile` — it carries the
   *  ``X-Telegram-Init-Data`` header that the API requires. */
  fileUrl: (jobId: number, kind: 'report' | 'catalog' | 'track'): string =>
    `${API_BASE}/jobs/${jobId}/files/${kind}`,

  /** Download a job artefact via fetch (so the ``initData`` header is
   *  attached) and trigger a browser download via an in-memory
   *  ``<a href>`` blob URL. Returns when the click has fired; the
   *  caller can chain a HapticFeedback / toast.
   *
   *  Throws :class:`ApiError` on non-2xx responses so the UI can
   *  surface a meaningful message (404 / 403 are real possibilities
   *  in production — files can be missing from disk or paths
   *  poisoned). */
  async downloadFile(
    jobId: number,
    kind: 'report' | 'catalog' | 'track',
  ): Promise<void> {
    const response = await fetch(`${API_BASE}/jobs/${jobId}/files/${kind}`, {
      headers: { 'X-Telegram-Init-Data': getInitData() },
    });
    if (!response.ok) {
      let detail = response.statusText;
      try {
        const parsed = (await response.json()) as { detail?: string };
        if (parsed.detail) detail = parsed.detail;
      } catch {
        /* keep statusText */
      }
      throw new ApiError(response.status, detail);
    }
    const blob = await response.blob();
    const disposition = response.headers.get('Content-Disposition') ?? '';
    // RFC 5987 filename*=UTF-8''… preferred; fall back to plain filename="…".
    let filename = `job_${jobId}_${kind}`;
    const star = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
    const plain = /filename="([^"]+)"/i.exec(disposition);
    if (star?.[1]) filename = decodeURIComponent(star[1]);
    else if (plain?.[1]) filename = plain[1];

    const objectUrl = URL.createObjectURL(blob);
    try {
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } finally {
      // Defer revoke so Safari/Telegram in-app browser has time to
      // start the download.
      setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    }
  },

  // ----- Phase 6 — analytics screen aggregators. -----
  //
  // Each method threads the same ``AnalyticsFilters`` so the
  // three views refresh atomically when the user flips a filter.
  // ``gnss_configs`` is sent as a repeatable query param (FastAPI
  // collects them into a list automatically). ``tectonic_model``
  // omitted entirely when null (the «все» selection).

  getAnalyticsSummary: (filters: AnalyticsFilters) =>
    request<AnalyticsSummary>(
      '/analytics/summary',
      _analyticsParams(filters),
    ),

  getAnalyticsHeatmap: (filters: AnalyticsFilters) =>
    request<AnalyticsHeatmap>(
      '/analytics/heatmap',
      _analyticsParams(filters),
    ),

  getAnalyticsScatter: (filters: AnalyticsFilters) =>
    request<AnalyticsScatterResponse>(
      '/analytics/scatter',
      _analyticsParams(filters),
    ),

  // Phase 6.6 B — distinct gnss_systems combos in the DB. No filter
  // params: the accordion filter wants to show everything that
  // exists regardless of what the operator has already selected.
  getAvailableGnssConfigs: () =>
    request<AnalyticsAvailableConfigsResponse>(
      '/analytics/available-gnss-configs',
    ),

  // Phase B Block 2 — wall-clock processing time per GNSS config.
  // No filter params: the config IS the grouping key, and the
  // operator-facing question is global («how long does each take?»).
  getAnalyticsProcessingTime: () =>
    request<ProcessingTimeResponse>('/analytics/processing-time'),

  // Phase B Block 3 — service self-assessment adequacy (σ vs |dR|).
  // No filter params — the «is the service honest with itself?»
  // verdict is a single global number, not a per-filter slice.
  getAnalyticsSelfAssessment: () =>
    request<SelfAssessmentResponse>('/analytics/self-assessment'),

  // Phase I — activity heatmap by completion + observation date.
  // No filter params — the two date-axes are operator-facing
  // global views, AnalyticsFilters don't narrow them sensibly
  // (per-config / per-tectonic-model heatmaps would just clutter
  // a daily-activity surface).
  getAnalyticsActivity: () =>
    request<AnalyticsActivityResponse>('/analytics/activity'),

  // Phase 5 — every station with coordinates + per-station job
  // aggregates. Drives the map screen markers + bottom sheet.
  getMapStations: () => request<MapStationsResponse>('/map/stations'),
};

/** Phase 6 — translate ``AnalyticsFilters`` into the ``request``
 *  helper's loose query-param object. We drop nullish entries
 *  instead of sending them as empty strings (the backend's
 *  ``_coerce_tectonic_model`` would accept the empty string too,
 *  but a clean URL surface is nicer in the network tab). */
function _analyticsParams(
  filters: AnalyticsFilters,
): Record<string, string | string[]> {
  const out: Record<string, string | string[]> = {};
  if (filters.tectonic_model) out.tectonic_model = filters.tectonic_model;
  if (filters.gnss_configs && filters.gnss_configs.length > 0) {
    out.gnss_configs = filters.gnss_configs;
  }
  return out;
}
